/**
 * MembershipService - Centralized tier limits and quota enforcement
 *
 * Per AGENT_WORKFLOW_STANDARDS.md Section 8:
 * - Pre-check quotas before high-resource operations
 * - Provide actionable upgrade paths on limit exceeded
 */

import { db } from '@/services/firebase';
import { doc, getDoc, setDoc, updateDoc, increment, FieldValue, query, collection, where, getCountFromServer, runTransaction, getDocs, orderBy, limit } from 'firebase/firestore';
import { logger } from '@/utils/logger';
import { v4 as uuidv4 } from 'uuid';
import {
    CreditWalletSchema,
    CreditTransactionSchema,
    type CreditWallet,
    type CreditTransaction,
    type CreditTransactionType,
} from '@indii/shared';

export type MembershipTier = 'free' | 'pro' | 'founder' | 'enterprise' | 'start' | 'build' | 'scale';


/**
 * Daily usage tracking stored in Firestore
 * Path: users/{userId}/usage/{YYYY-MM-DD}
 */
export interface DailyUsage {
    date: string;              // YYYY-MM-DD
    imagesGenerated: number;
    videosGenerated: number;
    videoSecondsGenerated: number;
    storageUsedMB: number;     // Cumulative, not daily
    totalSpend: number;        // Track daily spend in USD
    updatedAt: number;         // Timestamp
}

export interface TierLimits {
    // Video limits (in seconds)
    maxVideoDuration: number;
    maxVideoGenerationsPerDay: number;

    // Image limits
    maxImagesPerDay: number;
    maxBatchSize: number;

    // Physical Design limits
    maxDesignResolution: number;     // max width/height in px
    maxExportDPI: number;
    hasCMYKSupport: boolean;

    // Storage limits (in MB)
    maxStorageMB: number;

    // Project limits
    maxProjects: number;

    // Cost limits
    maxDailySpend: number;

    // Feature flags
    hasAdvancedEditing: boolean;
    hasCustomBranding: boolean;
    hasPriorityQueue: boolean;
    hasAPIAccess: boolean;
}

const TIER_LIMITS: Record<MembershipTier, TierLimits> = {
    free: {
        maxVideoDuration: 8 * 60,          // 8 minutes
        maxVideoGenerationsPerDay: 5,
        maxImagesPerDay: 50,
        maxBatchSize: 4,
        maxDesignResolution: 1024,
        maxExportDPI: 150,
        hasCMYKSupport: false,
        maxStorageMB: 500,                  // 500 MB
        maxProjects: 3,
        maxDailySpend: 1.0,                 // $1.00 Daily Limit
        hasAdvancedEditing: false,
        hasCustomBranding: false,
        hasPriorityQueue: false,
        hasAPIAccess: false,
    },
    start: {
        maxVideoDuration: 15 * 60,         // 15 minutes
        maxVideoGenerationsPerDay: 15,
        maxImagesPerDay: 250,
        maxBatchSize: 8,
        maxDesignResolution: 2048,
        maxExportDPI: 300,
        hasCMYKSupport: true,
        maxStorageMB: 25 * 1024,           // 25 GB
        maxProjects: 10,
        maxDailySpend: 5.0,                 // $5.00 Daily Limit
        hasAdvancedEditing: false,
        hasCustomBranding: true,
        hasPriorityQueue: false,
        hasAPIAccess: false,
    },
    build: {
        maxVideoDuration: 60 * 60,         // 60 minutes
        maxVideoGenerationsPerDay: 50,
        maxImagesPerDay: 1000,
        maxBatchSize: 32,
        maxDesignResolution: 4096,          // 4K
        maxExportDPI: 300,
        hasCMYKSupport: true,
        maxStorageMB: 100 * 1024,          // 100 GB
        maxProjects: 50,
        maxDailySpend: 15.0,                // $15.00 Daily Limit
        hasAdvancedEditing: true,
        hasCustomBranding: true,
        hasPriorityQueue: true,
        hasAPIAccess: false,
    },
    scale: {
        maxVideoDuration: 180 * 60,        // 180 minutes
        maxVideoGenerationsPerDay: 200,
        maxImagesPerDay: 3000,
        maxBatchSize: 64,
        maxDesignResolution: 8192,          // 8K
        maxExportDPI: 600,
        hasCMYKSupport: true,
        maxStorageMB: 500 * 1024,          // 500 GB
        maxProjects: 200,
        maxDailySpend: 30.0,                // $30.00 Daily Limit
        hasAdvancedEditing: true,
        hasCustomBranding: true,
        hasPriorityQueue: true,
        hasAPIAccess: true,
    },
    pro: {
        maxVideoDuration: 60 * 60,         // 60 minutes
        maxVideoGenerationsPerDay: 50,
        maxImagesPerDay: 500,
        maxBatchSize: 16,
        maxDesignResolution: 4096,          // 4K
        maxExportDPI: 300,
        hasCMYKSupport: true,
        maxStorageMB: 10 * 1024,           // 10 GB
        maxProjects: 50,
        maxDailySpend: 10.0,                // $10.00 Daily Limit
        hasAdvancedEditing: true,
        hasCustomBranding: true,
        hasPriorityQueue: true,
        hasAPIAccess: false,
    },
    founder: {
        maxVideoDuration: 4 * 60 * 60,     // 4 hours (same as enterprise)
        maxVideoGenerationsPerDay: 500,
        maxImagesPerDay: 5000,
        maxBatchSize: 64,
        maxDesignResolution: 8192,          // 8K
        maxExportDPI: 600,
        hasCMYKSupport: true,
        maxStorageMB: 10 * 1024 * 1024,    // 10 TB
        maxProjects: -1,                    // Unlimited
        maxDailySpend: 500.0,               // $500.00 Daily Limit (pass-through at cost)
        hasAdvancedEditing: true,
        hasCustomBranding: true,
        hasPriorityQueue: true,
        hasAPIAccess: true,
    },
    enterprise: {
        maxVideoDuration: 4 * 60 * 60,     // 4 hours
        maxVideoGenerationsPerDay: 500,
        maxImagesPerDay: 5000,
        maxBatchSize: 64,
        maxDesignResolution: 8192,          // 8K
        maxExportDPI: 600,
        hasCMYKSupport: true,
        maxStorageMB: 100 * 1024,          // 100 GB
        maxProjects: -1,                    // Unlimited
        maxDailySpend: 100.0,               // $100.00 Daily Limit
        hasAdvancedEditing: true,
        hasCustomBranding: true,
        hasPriorityQueue: true,
        hasAPIAccess: true,
    },
};

class MembershipServiceImpl {
    private sessionSpend: number = 0;
    private readonly MAX_SESSION_SPEND = 10.00; // $10 hard cap per request/swarm session

    /**
     * Check if the current user is a builder/dev account.
     * Checks for the god_mode custom claim on Firebase Auth.
     * 
     * @returns True if the account has the god_mode claim or in DEV mode.
     */
    private async isBuilderAccount(): Promise<boolean> {
        // ALWAYS bypass limits in local development so the team can test without hitting budget caps
        // IMPORTANT: Do NOT bypass in Vitest test runs — tests need to exercise real limit enforcement
        // OPTIONAL: Bypass via explicit environment variable if the developer chooses
        if (import.meta.env.VITE_BYPASS_BUDGET_LIMITS === 'true') {
            return true;
        }

        try {
            const firebaseModule = await import('@/services/firebase');
            const currentUser = firebaseModule.auth.currentUser;

            // Check for god_mode custom claim on Firebase Auth
            if (currentUser && typeof currentUser.getIdTokenResult === 'function') {
                const tokenResult = await currentUser.getIdTokenResult();
                if (tokenResult?.claims?.god_mode === true) {
                    return true;
                }
            }

            return false;
        } catch {
            // STRICT SAFETY: No automatic bypass in DEV mode anymore.
            // Requires explicit opt-in via .env flag for high-cost testing.
            return import.meta.env.VITE_BYPASS_BUDGET_LIMITS === 'true';
        }
    }

    /**
     * Get limits for a specific tier.
     * @param tier - The membership tier.
     * @returns The TierLimits object for the given tier.
     */
    getLimits(tier: MembershipTier): TierLimits {
        return TIER_LIMITS[tier] || TIER_LIMITS.free;
    }

    /**
     * Get maximum video duration in frames for a tier (at specified fps).
     * @param tier - The membership tier.
     * @param fps - Frames per second (default 30).
     * @returns The total number of frames allowed.
     */
    getMaxVideoDurationFrames(tier: MembershipTier, fps: number = 30): number {
        const limits = this.getLimits(tier);
        return limits.maxVideoDuration * fps;
    }

    /**
     * Get maximum video duration in seconds for a tier.
     * @param tier - The membership tier.
     * @returns Maximum duration in seconds.
     */
    getMaxVideoDurationSeconds(tier: MembershipTier): number {
        return this.getLimits(tier).maxVideoDuration;
    }

    /**
     * Check if a duration (in seconds) is within tier limits.
     * @param tier - The membership tier.
     * @param durationSeconds - The duration to check.
     * @returns True if within limits.
     */
    isWithinVideoDurationLimit(tier: MembershipTier, durationSeconds: number): boolean {
        return durationSeconds <= this.getLimits(tier).maxVideoDuration;
    }

    /**
     * Check if user can perform an action based on tier features.
     * @param tier - The membership tier.
     * @param feature - The feature key to check.
     * @returns True if the feature is enabled for the tier.
     */
    canUseFeature(tier: MembershipTier, feature: keyof TierLimits): boolean {
        const limits = this.getLimits(tier);
        const value = limits[feature];
        return typeof value === 'boolean' ? value : value !== 0;
    }

    /**
     * Format duration for display (e.g., "8 minutes", "1 hour").
     * @param seconds - Seconds to format.
     * @returns Formatted string for UI.
     */
    formatDuration(seconds: number): string {
        if (seconds < 60) return `${seconds} seconds`;
        if (seconds < 3600) return `${Math.floor(seconds / 60)} minutes`;
        const hours = Math.floor(seconds / 3600);
        const mins = Math.floor((seconds % 3600) / 60);
        return mins > 0 ? `${hours} hour${hours > 1 ? 's' : ''} ${mins} min` : `${hours} hour${hours > 1 ? 's' : ''}`;
    }

    /**
     * Get tier display name.
     * @param tier - The membership tier.
     * @returns Human-readable tier name.
     */
    getTierDisplayName(tier: MembershipTier): string {
        return ({
            free: 'Free',
            pro: 'Pro',
            founder: 'Founder',
            enterprise: 'Enterprise'
        } as Record<MembershipTier, string>)[tier];
    }

    /**
     * Get upgrade message for a specific limit type.
     * @param currentTier - The user's current tier.
     * @param limitType - The type of limit being hit.
     * @returns A string offering an upgrade path.
     */
    getUpgradeMessage(currentTier: MembershipTier, limitType: 'video' | 'images' | 'storage' | 'projects' | 'resolution' | 'export'): string {
        const nextTier = currentTier === 'free' ? 'Pro' : 'Enterprise';

        const messages = {
            video: `Upgrade to ${nextTier} for longer video durations`,
            images: `Upgrade to ${nextTier} for more image generations`,
            storage: `Upgrade to ${nextTier} for more storage space`,
            projects: `Upgrade to ${nextTier} for more projects`,
            resolution: `Upgrade to ${nextTier} for high-resolution 4K generation`,
            export: `Upgrade to ${nextTier} for professional CMYK/300DPI exports`
        };

        return messages[limitType];
    }

    /**
     * Get the current organization's tier from the store.
     * This is a helper that integrates with the Zustand store.
     * 
     * @returns A promise resolving to the current MembershipTier.
     */
    async getCurrentTier(): Promise<MembershipTier> {
        try {
            const { useStore } = await import('@/core/store');
            const state = useStore.getState();

            // GOD MODE: Bypass via custom claim or Dev environment
            if (await this.isBuilderAccount()) {
                return 'enterprise';
            }

            const currentOrg = state.organizations.find(o => o.id === state.currentOrganizationId);
            return currentOrg?.plan || 'free';
        } catch {
            return 'free';
        }
    }

    // =========================================================================
    // USAGE TRACKING (Section 8 Compliance)
    // =========================================================================

    /**
     * Gets today's date in YYYY-MM-DD format for Firestore document indexing.
     * Uses the local timezone to match user-perceived daily quotas.
     * 
     * @returns A string representing the current date in YYYY-MM-DD format.
     */
    private getTodayKey(): string {
        return new Date().toISOString().split('T')[0]!;
    }

    /**
     * Retrieves the current authenticated User ID from the application store.
     * 
     * @returns A promise resolving to the user ID string or null if not authenticated.
     */
    private async getCurrentUserId(): Promise<string | null> {
        try {
            const { useStore } = await import('@/core/store');
            const state = useStore.getState();
            // Prefer Firebase Auth as canonical; profile can lag during cache/profile sync.
            const authUserId = (state as unknown as { user?: { uid?: string | null } | null }).user?.uid;
            const profileUserId =
                state.userProfile?.uid ||
                (state.userProfile?.id && state.userProfile.id !== 'pending' ? state.userProfile.id : null);

            return authUserId || profileUserId || null;
        } catch {
            return null;
        }
    }

    /**
     * Retrieves the daily usage record for a specific user.
     * If no record exists for today, returns a default empty usage object.
     * 
     * @param userId - The unique ID of the user to check.
     * @returns A promise resolving to the user's daily usage statistics.
     */
    async getDailyUsage(userId: string): Promise<DailyUsage> {
        const dateKey = this.getTodayKey();
        const usageRef = doc(db, 'users', userId, 'usage', dateKey);

        try {
            const snapshot = await getDoc(usageRef);
            if (snapshot.exists()) {
                return snapshot.data() as DailyUsage;
            }
        } catch (error: unknown) {
            logger.warn('[MembershipService] Failed to get usage:', error);
        }

        // Return default empty usage
        return {
            date: dateKey,
            imagesGenerated: 0,
            videosGenerated: 0,
            videoSecondsGenerated: 0,
            storageUsedMB: 0,
            totalSpend: 0,
            updatedAt: Date.now()
        };
    }

    /**
     * Increments usage counters (images, videos, or duration) for a user.
     * Creates a new document if it's the first action of the day.
     * 
     * @param userId - The unique ID of the user.
     * @param type - Which resource type to increment ('image' or 'video').
     * @param count - Number of units to increment (default: 1).
     * @param videoSeconds - If type is 'video', the duration in seconds to add.
     * @returns A promise that resolves when the usage is updated.
     */
    async incrementUsage(
        userId: string,
        type: 'image' | 'video',
        count: number = 1,
        videoSeconds?: number
    ): Promise<void> {
        const dateKey = this.getTodayKey();
        const usageRef = doc(db, 'users', userId, 'usage', dateKey);

        try {
            const snapshot = await getDoc(usageRef);

            if (snapshot.exists()) {
                // Update existing document
                const updates: { [key: string]: number | FieldValue } = {
                    updatedAt: Date.now()
                };

                if (type === 'image') {
                    updates.imagesGenerated = increment(count);
                } else if (type === 'video') {
                    updates.videosGenerated = increment(count);
                    if (videoSeconds) {
                        updates.videoSecondsGenerated = increment(videoSeconds);
                    }
                }

                await updateDoc(usageRef, updates);
            } else {
                // Create new document for today
                const newUsage: DailyUsage = {
                    date: dateKey,
                    imagesGenerated: type === 'image' ? count : 0,
                    videosGenerated: type === 'video' ? count : 0,
                    videoSecondsGenerated: type === 'video' && videoSeconds ? videoSeconds : 0,
                    storageUsedMB: 0,
                    totalSpend: 0,
                    updatedAt: Date.now()
                };

                await setDoc(usageRef, newUsage);
            }
        } catch (error: unknown) {
            logger.error('[MembershipService] Failed to increment usage:', error);
            // Don't throw - usage tracking shouldn't block generation
        }
    }

    /**
     * Helper to get local storage key for daily offline spend tracking.
     */
    private getOfflineSpendKey(): string {
        return `indii_offline_spend_${this.getTodayKey()}`;
    }

    /**
     * Retrieve accumulated offline spend from localStorage.
     */
    getLocalOfflineSpend(): number {
        if (typeof window === 'undefined' || !window.localStorage) {
            return 0;
        }
        const val = window.localStorage.getItem(this.getOfflineSpendKey());
        return val ? parseFloat(val) || 0 : 0;
    }

    /**
     * Write offline spend to localStorage.
     */
    private setLocalOfflineSpend(amount: number): void {
        if (typeof window !== 'undefined' && window.localStorage) {
            window.localStorage.setItem(this.getOfflineSpendKey(), amount.toString());
        }
    }

    /**
     * Records a monetary spend amount for a user's daily usage.
     * This is used for budget enforcement and tiered pass-through billing.
     * 
     * @param userId - The unique ID of the user.
     * @param amount - The USD amount to add to the daily spend.
     * @returns A promise that resolves when the spend is recorded.
     */
    async recordSpend(userId: string, amount: number): Promise<void> {
        const dateKey = this.getTodayKey();
        const usageRef = doc(db, 'users', userId, 'usage', dateKey);

        try {
            // Phase 4: Track session-wide spend for immediate circuit breaking
            this.sessionSpend += amount;

            // If offline, accumulate spend locally so we can track offline quota limits instantly
            if (typeof navigator !== 'undefined' && !navigator.onLine) {
                const currentOfflineSpend = this.getLocalOfflineSpend();
                this.setLocalOfflineSpend(currentOfflineSpend + amount);
                logger.info(`[MembershipService] Offline mode: accumulated $${amount} to offline spend ledger.`);
                return;
            }

            // Atomic update or create with merge to prevent race conditions
            await setDoc(usageRef, {
                date: dateKey,
                totalSpend: increment(amount),
                updatedAt: Date.now()
            }, { merge: true });
        } catch (error: unknown) {
            logger.error('[MembershipService] Failed to record spend:', error);
        }
    }

    /**
     * Flushes accumulated offline daily spend back to the Firestore server ledger when transitioning online.
     */
    async syncOfflineSpend(userId: string): Promise<void> {
        const offlineSpend = this.getLocalOfflineSpend();
        if (offlineSpend <= 0) return;

        logger.info(`[MembershipService] Syncing accumulated offline spend of $${offlineSpend.toFixed(4)} to Firestore.`);
        
        // Temporarily override navigator.onLine during sync to ensure recordSpend performs a network operation
        const originalOnLine = navigator.onLine;
        Object.defineProperty(navigator, 'onLine', { value: true, configurable: true });

        try {
            await this.recordSpend(userId, offlineSpend);
            this.setLocalOfflineSpend(0);
        } catch (err) {
            logger.error('[MembershipService] Failed to sync offline spend:', err);
        } finally {
            Object.defineProperty(navigator, 'onLine', { value: originalOnLine, configurable: true });
        }
    }

    /**
     * Verifies if an estimated operation cost fits within the user's daily budget.
     * Checks tier-specific limits and current daily spend totals.
     * 
     * @param estimatedCost - The predicted cost of the upcoming operation in USD.
     * @returns A promise resolving to an approval status, remaining budget, and whether explicit user consent is required.
     */
    async checkBudget(estimatedCost: number): Promise<{ allowed: boolean; remainingBudget: number; requiresApproval: boolean }> {
        // GOD MODE: Bypass for Builder
        if (await this.isBuilderAccount()) {
            return { allowed: true, remainingBudget: Infinity, requiresApproval: false };
        }

        const userId = await this.getCurrentUserId();
        if (!userId) {
            return { allowed: false, remainingBudget: 0, requiresApproval: false };
        }

        const tier = await this.getCurrentTier();
        const limits = this.getLimits(tier);
        
        let currentSpend = 0;
        const isOffline = typeof navigator !== 'undefined' && !navigator.onLine;

        if (isOffline) {
            // Offline Budget Gate: Combine local offline spend accumulator to prevent offline quota escapes
            currentSpend = this.getLocalOfflineSpend();
            logger.info(`[MembershipService] Offline budget check: current local spend is $${currentSpend}`);
        } else {
            // Attempt to sync any existing offline spend first before checking
            await this.syncOfflineSpend(userId);
            const usage = await this.getDailyUsage(userId);
            currentSpend = usage.totalSpend || 0;
        }

        // Use integer cents arithmetic for currency comparison to avoid float errors
        const currentSpendCents = Math.round(currentSpend * 100);
        const estimatedCostCents = Math.round(estimatedCost * 100);
        const maxSpendCents = Math.round(limits.maxDailySpend * 100);

        const remainingBudgetCents = maxSpendCents - currentSpendCents;
        const allowed = (currentSpendCents + estimatedCostCents) <= maxSpendCents;

        // EMERGENCY CIRCUIT BREAKER: Check session-wide limit
        if (this.sessionSpend + estimatedCost > this.MAX_SESSION_SPEND) {
            logger.error(`[MembershipService] EMERGENCY KILL: Session spend ($${this.sessionSpend.toFixed(2)}) + estimate ($${estimatedCost.toFixed(2)}) exceeds session safety limit ($${this.MAX_SESSION_SPEND.toFixed(2)})`);
            return { allowed: false, remainingBudget: remainingBudgetCents / 100, requiresApproval: false };
        }

        // Ledger Policy: User must approve every charge over $0.50
        const requiresApproval = estimatedCost > 0.50;

        return {
            allowed,
            remainingBudget: remainingBudgetCents / 100,
            requiresApproval
        };
    }

    /**
     * Checks the user's quota for a specific resource type.
     * Enforces daily caps for images, videos, and project counts.
     * 
     * @param type - The resource type to check ('image', 'video', 'storage', 'projects').
     * @param amount - The number of units being requested (default: 1).
     * @returns A promise resolving to an 'allowed' status, current usage, and the maximum allowed units.
     */
    async checkQuota(
        type: 'image' | 'video' | 'storage' | 'projects',
        amount: number = 1
    ): Promise<{ allowed: boolean; currentUsage: number; maxAllowed: number }> {
        const userId = await this.getCurrentUserId();
        if (!userId) {
            // No user = deny quota (must be authenticated for any generation)
            logger.warn('[MembershipService] Quota check denied: No authenticated user');
            return { allowed: false, currentUsage: 0, maxAllowed: 0 };
        }

        // GOD MODE: Bypass via custom claim or Dev environment
        if (await this.isBuilderAccount()) {
            return { allowed: true, currentUsage: 0, maxAllowed: Infinity };
        }

        const tier = await this.getCurrentTier();
        const limits = this.getLimits(tier);
        const usage = await this.getDailyUsage(userId);

        let currentUsage: number;
        let maxAllowed: number;

        switch (type) {
            case 'image':
                currentUsage = usage.imagesGenerated;
                maxAllowed = limits.maxImagesPerDay;
                break;
            case 'video':
                currentUsage = usage.videosGenerated;
                maxAllowed = limits.maxVideoGenerationsPerDay;
                break;
            case 'storage':
                currentUsage = usage.storageUsedMB;
                maxAllowed = limits.maxStorageMB;
                break;
            case 'projects':
                // Unlimited projects check — applies to any tier with maxProjects === -1
                // (currently both 'founder' and 'enterprise')
                if (limits.maxProjects === -1) {
                    return { allowed: true, currentUsage: 0, maxAllowed: Infinity };
                }

                try {
                    const { useStore } = await import('@/core/store');
                    const state = useStore.getState();
                    const currentOrgId = state.currentOrganizationId;

                    if (currentOrgId && currentOrgId !== 'personal') {
                        // Count org projects
                        const projectsRef = collection(db, 'projects');
                        const q = query(projectsRef, where('orgId', '==', currentOrgId));
                        const snapshot = await getCountFromServer(q);
                        currentUsage = snapshot.data().count;
                    } else {
                        // Count personal projects
                        const q = query(collection(db, 'projects'), where('orgId', '==', 'personal'), where('userId', '==', userId));
                        const snapshot = await getCountFromServer(q);
                        currentUsage = snapshot.data().count;
                    }
                } catch (_e: unknown) {
                    // ISSUE-1277: FAIL CLOSED. This previously set currentUsage = 0
                    // ("fail open but warn"), so a transient Firestore outage granted
                    // unlimited project creation on a limited tier. Matches the
                    // fail-closed policy already documented in SubscriptionService
                    // (ISSUE-886): a quota check that cannot verify usage must deny,
                    // not assume zero.
                    logger.warn("[MembershipService] Failed to count projects; blocking action (fail-closed):", _e);
                    return { allowed: false, currentUsage: 0, maxAllowed: limits.maxProjects };
                }

                maxAllowed = limits.maxProjects;
                break;
            default:
                return { allowed: true, currentUsage: 0, maxAllowed: Infinity };
        }

        // Any tier with unlimited (-1) for a resource type gets a pass
        if (maxAllowed === -1) {
            return { allowed: true, currentUsage, maxAllowed: Infinity };
        }

        const allowed = (currentUsage + amount) <= maxAllowed;
        return { allowed, currentUsage, maxAllowed };
    }

    /**
     * Validates if a proposed video duration is within the tier limits.
     * 
     * @param durationSeconds - The total duration in seconds being requested.
     * @returns A promise resolving to the validation status and tier metadata.
     */
    async checkVideoDurationQuota(durationSeconds: number): Promise<{
        allowed: boolean;
        maxDuration: number;
        tierName: string;
    }> {
        const tier = await this.getCurrentTier();
        const limits = this.getLimits(tier);
        const allowed = durationSeconds <= limits.maxVideoDuration;

        return {
            allowed,
            maxDuration: limits.maxVideoDuration,
            tierName: this.getTierDisplayName(tier)
        };
    }

    // ──────────────────────────────────────────────────────────────────────────
    // Phase 20: Micro-Transactions & Credit-Based Purchases
    // ──────────────────────────────────────────────────────────────────────────

    /**
     * Get credit balance for a user's wallet.
     * Path: users/{userId}/wallet/current
     */
    async getCreditBalance(userId?: string): Promise<number> {
        const uid = userId || await this.getCurrentUserId();
        if (!uid) return 0;

        try {
            const walletRef = doc(db, 'users', uid, 'wallet', 'current');
            const snap = await getDoc(walletRef);
            if (!snap.exists()) return 0;
            const data = snap.data();
            return typeof data.balanceCredits === 'number' ? Math.max(0, data.balanceCredits) : 0;
        } catch (err) {
            logger.warn('[MembershipService] Failed to get credit balance:', err);
            return 0;
        }
    }

    /**
     * Check if user can deduct a specified amount of credits.
     */
    async canDeductCredits(amount: number, userId?: string): Promise<{
        allowed: boolean;
        balance: number;
        required: number;
    }> {
        if (amount <= 0) return { allowed: true, balance: 0, required: 0 };
        const balance = await this.getCreditBalance(userId);
        return {
            allowed: balance >= amount,
            balance,
            required: amount,
        };
    }

    /**
     * Deduct credits from user's wallet for a specific task.
     * Invokes the server-authoritative deductCredits Cloud Function,
     * with client-side fallback for offline/emulator/mock test environments.
     */
    async deductCredits(
        amount: number,
        reason: string,
        referenceId?: string,
        userId?: string
    ): Promise<{
        success: boolean;
        balanceAfter: number;
        transactionId?: string;
        autoTopUpTriggered?: boolean;
        error?: string;
    }> {
        if (amount <= 0) {
            const currentBal = await this.getCreditBalance(userId);
            return { success: true, balanceAfter: currentBal };
        }

        const uid = userId || await this.getCurrentUserId();
        if (!uid) {
            return { success: false, balanceAfter: 0, error: 'User not authenticated' };
        }

        // Try server-authoritative Cloud Function first
        try {
            const { functions } = await import('@/services/firebase');
            const { httpsCallable } = await import('firebase/functions');
            if (functions) {
                const deductFn = httpsCallable<{
                    amount: number;
                    reason: string;
                    referenceId?: string;
                    userId?: string;
                }, {
                    success: boolean;
                    balanceAfter: number;
                    transactionId: string;
                    autoTopUpTriggered?: boolean;
                }>(functions, 'deductCredits');

                const resp = await deductFn({
                    amount,
                    reason,
                    referenceId,
                    userId: uid,
                });

                if (resp?.data?.success) {
                    return resp.data;
                }
            }
        } catch (callableErr: unknown) {
            logger.warn('[MembershipService] deductCredits callable failed, attempting transaction fallback:', callableErr);
        }

        const walletRef = doc(db, 'users', uid, 'wallet', 'current');
        const txId = uuidv4();
        const txRef = doc(db, 'users', uid, 'credit_transactions', txId);

        try {
            const result = await runTransaction(db, async (transaction) => {
                const snap = await transaction.get(walletRef);
                const currentBalance = snap.exists() && typeof snap.data().balanceCredits === 'number'
                    ? snap.data().balanceCredits
                    : 0;

                if (currentBalance < amount) {
                    throw new Error(`Insufficient credits: balance is ${currentBalance}, requested ${amount}`);
                }

                const balanceAfter = currentBalance - amount;
                const now = Date.now();
                const autoTopUpEnabled = snap.exists() ? !!snap.data().autoTopUp : false;
                const autoTopUpThreshold = snap.exists() && typeof snap.data().autoTopUpThreshold === 'number'
                    ? snap.data().autoTopUpThreshold
                    : 100;
                const autoTopUpTriggered = autoTopUpEnabled && balanceAfter <= autoTopUpThreshold;

                const walletUpdate: Partial<CreditWallet> = {
                    balanceCredits: balanceAfter,
                    updatedAt: now,
                };

                if (!snap.exists()) {
                    transaction.set(walletRef, {
                        userId: uid,
                        balanceCredits: balanceAfter,
                        autoTopUp: false,
                        autoTopUpThreshold: 100,
                        currency: 'USD',
                        createdAt: now,
                        updatedAt: now,
                    });
                } else {
                    transaction.update(walletRef, walletUpdate);
                }

                const creditTx: CreditTransaction = {
                    id: txId,
                    userId: uid,
                    type: 'CONSUMPTION',
                    amountCredits: -amount,
                    balanceAfter,
                    reason,
                    ...(referenceId ? { referenceId } : {}),
                    createdAt: now,
                };

                transaction.set(txRef, creditTx);

                return { balanceAfter, txId, autoTopUpTriggered };
            });

            return {
                success: true,
                balanceAfter: result.balanceAfter,
                transactionId: result.txId,
                autoTopUpTriggered: result.autoTopUpTriggered,
            };
        } catch (err: unknown) {
            const msg = err instanceof Error ? err.message : String(err);
            logger.warn('[MembershipService] Failed credit deduction:', err);
            const fallbackBal = await this.getCreditBalance(uid);
            return {
                success: false,
                balanceAfter: fallbackBal,
                error: msg,
            };
        }
    }

    /**
     * Add credits to user's wallet (e.g. from Stripe purchase or promotional grant).
     */
    async addCredits(
        amount: number,
        reason: string,
        type: CreditTransactionType = 'PURCHASE',
        referenceId?: string,
        userId?: string
    ): Promise<{
        success: boolean;
        balanceAfter: number;
        transactionId?: string;
        error?: string;
    }> {
        if (amount <= 0) {
            const currentBal = await this.getCreditBalance(userId);
            return { success: true, balanceAfter: currentBal };
        }

        const uid = userId || await this.getCurrentUserId();
        if (!uid) {
            return { success: false, balanceAfter: 0, error: 'User not authenticated' };
        }

        const walletRef = doc(db, 'users', uid, 'wallet', 'current');
        const txId = uuidv4();
        const txRef = doc(db, 'users', uid, 'credit_transactions', txId);

        try {
            const result = await runTransaction(db, async (transaction) => {
                const snap = await transaction.get(walletRef);
                const currentBalance = snap.exists() && typeof snap.data().balanceCredits === 'number'
                    ? snap.data().balanceCredits
                    : 0;

                const balanceAfter = currentBalance + amount;
                const now = Date.now();

                if (!snap.exists()) {
                    transaction.set(walletRef, {
                        userId: uid,
                        balanceCredits: balanceAfter,
                        autoTopUp: false,
                        autoTopUpThreshold: 100,
                        currency: 'USD',
                        createdAt: now,
                        updatedAt: now,
                    });
                } else {
                    transaction.update(walletRef, {
                        balanceCredits: balanceAfter,
                        updatedAt: now,
                    });
                }

                const creditTx: CreditTransaction = {
                    id: txId,
                    userId: uid,
                    type,
                    amountCredits: amount,
                    balanceAfter,
                    reason,
                    ...(referenceId ? { referenceId } : {}),
                    createdAt: now,
                };

                transaction.set(txRef, creditTx);

                return { balanceAfter, txId };
            });

            return {
                success: true,
                balanceAfter: result.balanceAfter,
                transactionId: result.txId,
            };
        } catch (err: unknown) {
            const msg = err instanceof Error ? err.message : String(err);
            logger.error('[MembershipService] Failed credit addition:', err);
            const fallbackBal = await this.getCreditBalance(uid);
            return {
                success: false,
                balanceAfter: fallbackBal,
                error: msg,
            };
        }
    }

    /**
     * Create a Stripe Checkout session for a credit pack top-up via createMicroTransaction Cloud Function.
     */
    async createCreditCheckoutSession(
        packId: string,
        credits: number,
        returnUrl?: string,
        userId?: string
    ): Promise<{ checkoutUrl: string; sessionId: string }> {
        const uid = userId || await this.getCurrentUserId();
        if (!uid) {
            throw new Error('User not authenticated');
        }

        const baseUrl = returnUrl || (typeof window !== 'undefined' ? window.location.origin : '');
        const successUrl = `${baseUrl}?payment=success&pack=${encodeURIComponent(packId)}`;
        const cancelUrl = `${baseUrl}?payment=cancelled&pack=${encodeURIComponent(packId)}`;

        const { functions } = await import('@/services/firebase');
        const { httpsCallable } = await import('firebase/functions');
        if (!functions) {
            throw new Error('Firebase functions not initialized');
        }

        const createMicroTxFn = httpsCallable<{
            userId: string;
            credits: number;
            successUrl: string;
            cancelUrl: string;
        }, {
            checkoutUrl: string;
            sessionId: string;
        }>(functions, 'createMicroTransaction');

        const result = await createMicroTxFn({
            userId: uid,
            credits,
            successUrl,
            cancelUrl,
        });

        return result.data;
    }


    /**
     * Retrieve recent credit transactions for a user, ordered from newest to oldest.
     */
    async getCreditTransactions(limitCount: number = 20, userId?: string): Promise<CreditTransaction[]> {
        const uid = userId || await this.getCurrentUserId();
        if (!uid) return [];

        try {
            const txCollection = collection(db, 'users', uid, 'credit_transactions');
            const q = query(txCollection, orderBy('createdAt', 'desc'), limit(limitCount));
            const snap = await getDocs(q);
            return snap.docs.map(d => d.data() as CreditTransaction);
        } catch (err) {
            logger.warn('[MembershipService] Failed to get credit transactions:', err);
            return [];
        }
    }

    /**
     * Retrieve user full credit wallet document.
     */
    async getCreditWallet(userId?: string): Promise<CreditWallet | null> {
        const uid = userId || await this.getCurrentUserId();
        if (!uid) return null;

        try {
            const walletRef = doc(db, 'users', uid, 'wallet', 'current');
            const snap = await getDoc(walletRef);
            if (!snap.exists()) return null;
            return snap.data() as CreditWallet;
        } catch (err) {
            logger.warn('[MembershipService] Failed to get credit wallet:', err);
            return null;
        }
    }

    /**
     * Update user credit wallet configuration (auto top-up preferences).
     * Invokes server-authoritative updateWalletSettings Cloud Function with client fallback.
     */
    async updateWalletSettings(
        settings: {
            autoTopUp: boolean;
            autoTopUpThreshold?: number;
            autoTopUpPackId?: string;
        },
        userId?: string
    ): Promise<{ success: boolean; wallet?: CreditWallet; error?: string }> {
        const uid = userId || await this.getCurrentUserId();
        if (!uid) {
            return { success: false, error: 'User not authenticated' };
        }

        try {
            const { functions } = await import('@/services/firebase');
            const { httpsCallable } = await import('firebase/functions');
            if (functions) {
                const updateFn = httpsCallable<{
                    userId: string;
                    autoTopUp: boolean;
                    autoTopUpThreshold?: number;
                    autoTopUpPackId?: string;
                }, {
                    success: boolean;
                    wallet: CreditWallet;
                }>(functions, 'updateWalletSettings');

                const resp = await updateFn({
                    userId: uid,
                    ...settings,
                });

                if (resp?.data?.success) {
                    return resp.data;
                }
            }
        } catch (callableErr: unknown) {
            logger.warn('[MembershipService] updateWalletSettings callable failed, attempting transaction fallback:', callableErr);
        }

        // Offline / dev fallback: Direct Firestore update
        try {
            const walletRef = doc(db, 'users', uid, 'wallet', 'current');
            const snap = await getDoc(walletRef);
            const now = Date.now();

            let updatedWallet: CreditWallet;
            if (!snap.exists()) {
                updatedWallet = {
                    userId: uid,
                    balanceCredits: 0,
                    autoTopUp: settings.autoTopUp,
                    autoTopUpThreshold: settings.autoTopUpThreshold ?? 100,
                    ...(settings.autoTopUpPackId ? { autoTopUpPackId: settings.autoTopUpPackId } : {}),
                    currency: 'USD',
                    createdAt: now,
                    updatedAt: now,
                };
                await setDoc(walletRef, updatedWallet);
            } else {
                const existing = snap.data() as CreditWallet;
                updatedWallet = {
                    ...existing,
                    autoTopUp: settings.autoTopUp,
                    autoTopUpThreshold: settings.autoTopUpThreshold ?? existing.autoTopUpThreshold ?? 100,
                    ...(settings.autoTopUpPackId ? { autoTopUpPackId: settings.autoTopUpPackId } : {}),
                    updatedAt: now,
                };
                await updateDoc(walletRef, {
                    autoTopUp: updatedWallet.autoTopUp,
                    autoTopUpThreshold: updatedWallet.autoTopUpThreshold,
                    ...(updatedWallet.autoTopUpPackId ? { autoTopUpPackId: updatedWallet.autoTopUpPackId } : {}),
                    updatedAt: now,
                });
            }

            return { success: true, wallet: updatedWallet };
        } catch (err: unknown) {
            const msg = err instanceof Error ? err.message : String(err);
            logger.error('[MembershipService] Failed to update wallet settings:', err);
            return { success: false, error: msg };
        }
    }

    /**
     * Deducts credits for a standardized platform consumable feature.
     */
    async consumeCreditsForFeature(
        feature: ConsumableFeatureKey,
        referenceId?: string,
        userId?: string
    ): Promise<{
        success: boolean;
        balanceAfter?: number;
        costCredits: number;
        featureName: string;
        transactionId?: string;
        autoTopUpTriggered?: boolean;
        error?: string;
    }> {
        const config = CONSUMABLE_FEATURE_COSTS[feature];
        if (!config) {
            return {
                success: false,
                costCredits: 0,
                featureName: String(feature),
                error: `Unknown feature key: ${String(feature)}`,
            };
        }

        const uid = userId || await this.getCurrentUserId();
        if (!uid) {
            return {
                success: false,
                costCredits: config.credits,
                featureName: config.name,
                error: 'User not authenticated',
            };
        }

        const preflight = await this.canDeductCredits(config.credits, uid);
        if (!preflight.allowed) {
            return {
                success: false,
                costCredits: config.credits,
                featureName: config.name,
                balanceAfter: preflight.balance,
                error: `Insufficient credits. Required: ${config.credits}, Available: ${preflight.balance}`,
            };
        }

        const deduction = await this.deductCredits(
            config.credits,
            config.name,
            referenceId,
            uid
        );

        if (!deduction.success) {
            return {
                success: false,
                costCredits: config.credits,
                featureName: config.name,
                balanceAfter: deduction.balanceAfter,
                error: deduction.error || 'Credit deduction failed',
            };
        }

        return {
            success: true,
            balanceAfter: deduction.balanceAfter,
            costCredits: config.credits,
            featureName: config.name,
            transactionId: deduction.transactionId,
            autoTopUpTriggered: deduction.autoTopUpTriggered,
        };
    }
}

export const CONSUMABLE_FEATURE_COSTS = {
    cover_art: { credits: 50, name: 'Cover Art & 4K Upscale Generation' },
    mastering: { credits: 100, name: 'Audio Mastering & Loudness Optimization' },
    distribution: { credits: 250, name: 'Global DSP Distribution Package Delivery' },
    legal_splits: { credits: 75, name: 'Legal Splits & Autonomous Claims Review' },
} as const;

export type ConsumableFeatureKey = keyof typeof CONSUMABLE_FEATURE_COSTS;

export const MembershipService = new MembershipServiceImpl();

// Export tier limits for direct access if needed
export { TIER_LIMITS };
